import { useEffect, useState } from "react";
import { getIn, useFormikContext } from "formik";
import ZapSignActivate from "../../UploadInfoApis/ZapSignActivate";
import VelotrackActivate from "../../UploadInfoApis/VelotrackActivate";
import { createContract } from "../../../../../services/integration.api";
import ZapSignConfirmation from "../../UploadInfoApis/ZapSignConfirmation";
import { getPlanDays, getPlanLabel, getPlanMonths } from "./AdditionsPlan";
import { ImageBondVehicleRenterRow } from "./field/ImageBondVehicleRenterRow";
import { IRenter, SignatureInfo } from "../../../../../../../types/renter.type";
import { BondRenterVehicle } from "../../../../../../../common/services/renters.api";
import { saveVehicleRecords } from "../../../../../../../common/services/vehicles.api";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import SlideToggle from "../../../../../../../common/components/ui/toggleSwitch/SlideToggle";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import { AvailableVehiclesFieldFormik } from "../../../../forms/fields/AvailableVehiclesFieldFormik";

type Period = {
  value: string;
  label: string;
  months: number;
  days?: number;
};

type Props = {
  data: IRenter;
};

export default function ChangeVehicleModal(props: Props) {
  const { notifySuccess, notifyError } = useToastContext();
  const renter = props.data;
  const formik = useFormikContext();
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLicensePlateEmpty, setIsLicensePlateEmpty] = useState(false);
  const [isCheckOption, setIsCheckOption] = useState(false);

  const [complement, setComplement] = useState("");
  const [signatureInfo, setSignatureInfo] = useState<SignatureInfo>();

  // Valores do formulário
  const registers = getIn(formik.values, "registers");
  const renterId = getIn(formik.values, "renterId");
  const reason = getIn(formik.values, "motive");
  const additions = getIn(formik.values, "additions");
  const vehicle = getIn(formik.values, "vehicle");
  const vehicleId = getIn(formik.values, "vehicle._id");

  const licensePlate = renter.activationData?.vehicle.licensePlate;
  const plantype = renter.activationData?.plan.planType;

  // Defina os registros iniciais
  const initialRegisters = [
    { type: "Frente do veículo", content: "" },
    { type: "Lateral direita do veículo", content: "" },
    { type: "Lateral esquerda do veículo", content: "" },
    { type: "Traseira do veículo", content: "" },
    { type: "Odômetro", content: "" },
  ];

  // Informações do plano
  const period: Period = {
    value: plantype || "",
    label: getPlanLabel(plantype || ""),
    months: getPlanMonths(plantype || ""),
    days: getPlanDays(plantype || ""),
  };

  // Atualize o estado isLicensePlateEmpty apenas quando licensePlate muda
  useEffect(() => {
    setIsLicensePlateEmpty(!licensePlate);
  }, [licensePlate]);

  // Limpar e redefinir os registros quando o modal é montado
  useEffect(() => {
    formik.setFieldValue("registers", initialRegisters);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler para vínculo do veículo e upload de fotos
  const handleSavePhotos = async () => {
    setIsLoading(true);
    try {
      // Dados do vínculo
      const bondData = {
        renterId,
        vehicleId,
        isCheckOption,
      };

      const resultBondVehicle = await BondRenterVehicle(bondData);

      if (!resultBondVehicle.ok) {
        notifyError(resultBondVehicle.message as string);
        return;
      }
      notifySuccess("Veículo Vinculado com sucesso");

      // Dados das fotos
      const savePhotoDelivery = {
        renterId,
        vehicleId,
        photos: registers,
        nameRenter: renter.name,
        licensePlate: vehicle?.licensePlate,
        switchDescription: complement,
        switchReason: reason,
      };

      const resultPhoto = await saveVehicleRecords(savePhotoDelivery);

      if (!resultPhoto.ok) {
        notifyError(resultPhoto.message as string);
        return;
      }

      notifySuccess("Fotos salvas com sucesso");
    } catch (error) {
      notifyError("Ocorreu um erro ao salvar as fotos");
    } finally {
      setIsLoading(false);
    }
  };

  // Handler para assinatura
  function handleSignature(contractInfo) {
    setSignatureInfo(contractInfo);
  }

  // Dados do contrato
  const contractData = {
    renterId,
    vehicleId,
    period,
    additions,
  };

  return (
    <section className="flex flex-col gap-4">
      <div>
        <>
          {isLicensePlateEmpty ? (
            <div
              className="bg-bluePrimary/20 border-l-4 border-yellow-500 p-4"
              role="alert"
            >
              <p className="font-bold">Atenção</p>
              <p>O cliente está apto a receber uma nova moto!</p>
              <button
                className="bg-redPrimary text-white font-semibold p-2 w-[100px] rounded-md mt-2"
                type="button"
                onClick={() => setShowForm(true)}
              >
                Vincular
              </button>
            </div>
          ) : (
            <div
              className="bg-redPrimary/20 border-l-4 border-red-500 p-4"
              role="alert"
            >
              <p className="font-bold">Atenção</p>
              <p>O cliente não está apto a receber uma nova moto!</p>
            </div>
          )}
        </>

        {(showForm || isCheckOption) && (
          <div className="mt-2 border-t pt-2">
            <ComboBoxFormik
              name="reason"
              label="Motivo da troca"
              options={[
                { value: "", label: "-" },
                { value: "Acidente", label: "Acidente" },
                {
                  value: "AtualizaçãoContrato:",
                  label: "Atualização do Contrato",
                },
                { value: "NovaMoto", label: "Entrega da Nova Moto" },
                { value: "MotoReserva", label: "Entrega de Moto Reserva" },
                { value: "Outro", label: "Outro" },
              ]}
              required={true}
            />

            <div className="flex flex-col gap-1 mt-2">
              <label
                className="block text-sm font-medium text-gray-600"
                htmlFor="otherReason"
              >
                Complemento
              </label>
              <textarea
                id="otherReason"
                name="otherReason"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
                style={{ resize: "none", height: "90px" }}
                className="border rounded p-2"
              />
            </div>

            <div className="mt-2">
              <h2 className="text-2xl font-semibold ">Vinculo da nova moto</h2>
            </div>
            <AvailableVehiclesFieldFormik
              listAll={false}
              companyId={renter.companyId}
              renterId={renter._id}
              required={true}
            />
            <div className="pb-3 flex items-center justify-center">
              <span
                className={`font-semibold text-[14px] mr-4 ${
                  !isCheckOption ? "text-blue-600" : "text-gray-500"
                }`}
              >
                Troca de Moto (Definitivamente)
              </span>

              <SlideToggle
                name="isCheckOption"
                onToggle={(checked) => {
                  setIsCheckOption(checked);
                  formik.setFieldValue("isCheckOption", checked);

                  // Mostrar formulário automaticamente quando for Moto Reserva
                  if (checked) {
                    setShowForm(true);
                  }
                }}
              />

              <span
                className={`font-semibold text-[14px] ml-4 ${
                  isCheckOption ? "text-blue-600" : "text-gray-500"
                }`}
              >
                Moto Reserva (Temporariamente)
              </span>
            </div>

            <div className="grid gap-2">
              <h2 className="text-2xl font-semibold">Registros do veículo</h2>
              {(!vehicle || !vehicleId) && (
                <small className="text-redPrimary">
                  Selecione um veículo para adicionar as fotos
                </small>
              )}

              {registers?.map((doc, index) => (
                <div
                  key={index}
                  className={`space-x-3 px-3 py-2 my-0 rounded ${
                    index % 2 ? "bg-gray/5" : "bg-gray/25"
                  }`}
                >
                  <ImageBondVehicleRenterRow
                    initialData={doc || {}}
                    name={`registers.${index}`}
                    onRemoveClick={() =>
                      formik.setFieldValue(
                        "registers",
                        registers.filter((_, i) => i !== index)
                      )
                    }
                    prefix="entrega"
                  />
                </div>
              ))}

              <button
                type="button"
                className="disabled:text-black/50"
                onClick={() => {
                  formik.setFieldValue(`registers.${registers.length}`, {
                    type: "",
                    content: "",
                    verified: false,
                  });
                }}
              >
                + registro
              </button>
            </div>

            <div className="flex justify-end mb-6 mt-4">
              <ButtonWithLoader
                type="button"
                onClick={handleSavePhotos}
                isLoading={isLoading}
                disabled={!vehicle || !vehicleId}
              >
                Vincular
              </ButtonWithLoader>
            </div>

            <div className="mt-4">
              <VelotrackActivate
                onSave={() => {
                  // Implementar lógica de salvamento do Velotrack
                }}
                renterId={renterId}
              />
            </div>

            <div className="mt-4">
              <ZapSignActivate
                disabled={!vehicle || !vehicleId}
                onSave={() => {
                  // Implementar lógica de salvamento do ZapSign
                }}
                contractData={contractData}
                handleSignature={handleSignature}
                contractFn={createContract}
              />
            </div>

            <div className="mt-4">
              {signatureInfo?.ok && (
                <ZapSignConfirmation contractInfo={signatureInfo} />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
